import { useMemo, useRef, useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import { Plus, Minus, RotateCcw } from "lucide-react";
import iso2ToNum from "../data/iso3166_alpha2_to_numeric.json";

// Topojson served de public/ para respeitar a mesma origin (evita CORS)
const TOPO_URL = "/world-countries.json";

/**
 * Mapa mundial com heat colours proporcional a falhas (com fallback para sucessos).
 * buckets: [{country, country_code, failures, successes, distinct_ips}, ...]
 * Zoom controlado via botoes +/-. Scroll da roda esta desactivado para nao
 * interferir com o scroll da pagina.
 */
export default function ThreatMap({ buckets = [], metric = "failures" }) {
  const [tooltip, setTooltip] = useState(null); // {x,y,label,failures,successes,ips}
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState([10, 20]);
  const containerRef = useRef(null);

  const { byNumeric, maxValue } = useMemo(() => {
    const m = {};
    let max = 0;
    for (const b of buckets) {
      const num = iso2ToNum[(b.country_code || "").toUpperCase()];
      if (!num) continue;
      const v = metric === "successes" ? b.successes : b.failures;
      if (v > max) max = v;
      m[num] = b;
    }
    return { byNumeric: m, maxValue: max };
  }, [buckets, metric]);

  // Escala: cinza claro -> vermelho quente (para failures) ou verde (successes)
  const colorScale = useMemo(() => {
    const range = metric === "successes"
      ? ["#e2e8f0", "#a7f3d0", "#34d399", "#059669"]
      : ["#e2e8f0", "#fecaca", "#f87171", "#b91c1c"];
    return scaleLinear().domain([0, Math.max(1, maxValue * 0.25),
                                  Math.max(2, maxValue * 0.6), Math.max(3, maxValue)])
                        .range(range);
  }, [maxValue, metric]);

  const zoomIn = () => setZoom((z) => Math.min(5, +(z + 0.5).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(1, +(z - 0.5).toFixed(2)));
  const reset = () => { setZoom(1); setCenter([10, 20]); };

  return (
    <div className="relative" ref={containerRef} data-testid="threat-world-map">
      <ComposableMap projection="geoEqualEarth"
        projectionConfig={{ scale: 165 }}
        style={{ width: "100%", height: "auto" }}>
        <ZoomableGroup center={center} zoom={zoom} maxZoom={5}
          onMoveEnd={({ coordinates, zoom: z }) => { setCenter(coordinates); setZoom(z); }}
          filterZoomEvent={(evt) => {
            // Desactiva zoom por scroll (wheel) para nao interferir com scroll da pagina.
            // Continua permitir pan via drag do rato.
            if (!evt) return true;
            return evt.type !== "wheel";
          }}>
          <Geographies geography={TOPO_URL}>
            {({ geographies }) => geographies.map((geo) => {
              const num = String(geo.id).padStart(3, "0");
              const b = byNumeric[num];
              const v = b ? (metric === "successes" ? b.successes : b.failures) : 0;
              const fill = b ? colorScale(v) : "#f1f5f9";
              return (
                <Geography key={geo.rsmKey} geography={geo}
                  fill={fill}
                  stroke="#cbd5e1"
                  strokeWidth={0.35}
                  onMouseEnter={(e) => {
                    if (!b) return;
                    setTooltip({
                      x: e.clientX, y: e.clientY,
                      label: b.country || geo.properties?.name || "-",
                      country_code: b.country_code,
                      failures: b.failures, successes: b.successes,
                      distinct_ips: b.distinct_ips,
                    });
                  }}
                  onMouseMove={(e) => setTooltip((t) => t ? { ...t, x: e.clientX, y: e.clientY } : t)}
                  onMouseLeave={() => setTooltip(null)}
                  style={{
                    default: { outline: "none", cursor: b ? "pointer" : "default", transition: "fill 200ms" },
                    hover: { fill: b ? "#1e293b" : "#e2e8f0", outline: "none" },
                    pressed: { outline: "none" },
                  }} />
              );
            })}
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {/* Controles de zoom (canto superior direito) */}
      <div className="absolute top-2 right-2 flex flex-col gap-1 bg-white/90 backdrop-blur border border-tint rounded-lg shadow-sm p-1" data-testid="threat-map-zoom-controls">
        <button onClick={zoomIn} disabled={zoom >= 5} data-testid="threat-map-zoom-in"
          title="Aproximar"
          className="w-7 h-7 flex items-center justify-center text-navy hover:bg-slate-100 rounded disabled:opacity-30">
          <Plus size={14} />
        </button>
        <button onClick={zoomOut} disabled={zoom <= 1} data-testid="threat-map-zoom-out"
          title="Afastar"
          className="w-7 h-7 flex items-center justify-center text-navy hover:bg-slate-100 rounded disabled:opacity-30">
          <Minus size={14} />
        </button>
        <button onClick={reset} disabled={zoom === 1} data-testid="threat-map-zoom-reset"
          title="Repor vista"
          className="w-7 h-7 flex items-center justify-center text-navy hover:bg-slate-100 rounded disabled:opacity-30">
          <RotateCcw size={12} />
        </button>
      </div>

      {/* Legenda */}
      <div className="absolute bottom-2 left-2 flex items-center gap-2 bg-white/85 backdrop-blur border border-tint rounded-lg px-2 py-1.5 text-[10px] text-navy shadow-sm">
        <span className="font-semibold uppercase tracking-wide">{metric === "successes" ? "Sucessos" : "Falhas"}</span>
        <div className="flex items-center">
          {[0, 0.25, 0.6, 1].map((f) => (
            <span key={f} className="w-4 h-3" style={{ background: colorScale(f * Math.max(1, maxValue)) }} />
          ))}
        </div>
        <span className="text-muted2">0</span>
        <span className="text-muted2">{maxValue}</span>
      </div>

      {tooltip && (
        <div className="fixed z-50 pointer-events-none bg-navy text-white text-[11px] px-2.5 py-1.5 rounded shadow-lg"
          style={{ left: tooltip.x + 12, top: tooltip.y + 12 }}>
          <div className="font-semibold">{tooltip.label} {tooltip.country_code ? `(${tooltip.country_code})` : ""}</div>
          <div className="flex gap-2 mt-0.5">
            <span className="text-red-300">{tooltip.failures} falhas</span>
            <span className="text-emerald-300">{tooltip.successes} sucessos</span>
            <span className="text-slate-300">{tooltip.distinct_ips} IPs</span>
          </div>
        </div>
      )}
    </div>
  );
}
